import { useLocalization } from '@capsplus/mfe-common/hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { iconsRegistry } from '../components/icons/IconsRegistry';
import {
  getCurrentLoginInformation,
  getUsersMenuItems,
  isUserSessionActive,
  loginToCapsPlus,
} from '../services/authService';

const DashboardIcon = iconsRegistry['Dashboard'];
const UsersIcon = iconsRegistry['Users'];

export const useLoadDataUser = (authState) => {
  const { L } = useLocalization();
  const loadingRef = useRef(false);
  const [userDataLoaded, setUserDataLoaded] = useState(false);
  const [isLoadingUserData, setIsLoadingUserData] = useState(false);
  const [activeUserSession, setActiveUserSession] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [activeUser, setActiveUser] = useState(null);

  const loadUserData = useCallback(async () => {
    if (loadingRef.current || userDataLoaded) {
      console.log('User data already loading or loaded, skipping...');
      return;
    }

    if (!authState?.isAuthenticated) {
      console.log('User not authenticated, skipping data load');
      return;
    }

    loadingRef.current = true;
    setIsLoadingUserData(true);

    try {
      const sessionActive = await isUserSessionActive().catch(() => false);
      setActiveUserSession(sessionActive);
      if (!sessionActive) {
        console.log('Session not active, exchanging tokens...');
        const oktaAccessToken = await authState.accessToken;
        if (oktaAccessToken) {
          await loginToCapsPlus(oktaAccessToken);
          setActiveUserSession(true);
        }
      }

      // Load user permissions and menu items
      const [userMenuItems, currentUser] = await Promise.allSettled([
        getUsersMenuItems(),
        getCurrentLoginInformation(),
      ]);
      console.log('my data', { userMenuItems, currentUser });

      if (userMenuItems.status === 'fulfilled') {
        setMenuItems(userMenuItems.value || []);
      } else {
        console.warn('Failed to load menu items:', userMenuItems.reason);
        // Fallback menu items
        setMenuItems([
          {
            id: 'dashboard',
            name: 'dashboard',
            displayName: L('Dashboard'),
            icon: <DashboardIcon />,
          },
          {
            id: 'users',
            name: 'users',
            displayName: L('Users'),
            icon: <UsersIcon />,
          },
        ]);
      }

      // Set current user info
      if (currentUser.status === 'fulfilled') {
        setActiveUser(currentUser.value || null);
      } else {
        console.warn('Failed to load current user info:', currentUser.reason);
        setActiveUser(null);
      }

      setUserDataLoaded(true); // Mark as loaded
      console.log('User data loaded successfully');
    } catch (error) {
      console.error('Error loading user data:', error);
      setMenuItems([
        {
          id: 'dashboard',
          name: 'dashboard',
          displayName: L('Dashboard'),
          icon: '📊',
        },
        { id: 'users', name: 'users', displayName: L('Users'), icon: '👥' },
      ]);
      setUserDataLoaded(true); // Mark as loaded even on error
    } finally {
      setIsLoadingUserData(false);
      loadingRef.current = false;
    }
  }, [authState?.isAuthenticated, userDataLoaded, L, authState.accessToken]);

  // Simplified useEffect - only runs when authentication state changes
  useEffect(() => {
    console.log(
      'useEffect triggered - AuthState changed:',
      authState?.isAuthenticated
    );

    // Reset data loaded state when auth state changes
    if (authState?.isAuthenticated && !userDataLoaded) {
      loadUserData();
    } else if (!authState?.isAuthenticated) {
      // Reset state when user logs out
      setUserDataLoaded(false);
      setMenuItems([]);
    }
  }, [authState?.isAuthenticated, loadUserData, userDataLoaded]);

  return {
    isLoadingUserData,
    activeUserSession,
    menuItems,
    activeUser,
    userDataLoaded,
  };
};
